import axiosInstance from "config/axios";
import { FileObject } from "mui-file-dropzone";
import Papa, { ParseError, ParseResult } from "papaparse";
import { Store } from "pullstate";
import {
  FileColumnMapping,
  FileDataRequest,
  CreateFileJobRequestExtended,
  MigrationJobService,
  ResponseCode,
} from "services/KnowfullyAdminClient";
import { Context } from "types/common-types";

const FILE_CHUNK_SIZE = 10000; // size in bytes

type ImportsUploadStore = {
  context: Context | null;
  data: unknown[] | null;
  columns?: string[];
  errors: ParseError[];
  mappings: FileColumnMapping[];
  file: File | undefined;
  fileObject: FileObject | undefined;
  jobId: string | undefined;
};

type DataImportBulkUploadStore = {
  bulkUploadProgressStatus?: number;
  showBulkUploadProgress: boolean;
  showSuccessSnackBar: boolean;
  shouldAbortUpload: boolean;
  cancelledSnackbarMessageId?: string;
  showCancelledSnackbar: boolean;
  showErrorSnackbar: boolean;
  uploadCompleted: boolean;
  errorSnackbarMessage: { field: string; value: string };
};

const importsUploadStore = new Store<ImportsUploadStore>({
  context: null,
  data: null,
  columns: [],
  errors: [],
  mappings: [],
  file: undefined,
  jobId: undefined,
  fileObject: undefined,
});

export const importsBulkUploadStore = new Store<DataImportBulkUploadStore>({
  showBulkUploadProgress: false,
  showSuccessSnackBar: false,
  shouldAbortUpload: false,
  showCancelledSnackbar: false,
  showErrorSnackbar: false,
  uploadCompleted: false,
  errorSnackbarMessage: { field: "", value: "" },
});

export const importFile = async (
  context: Context | null,
  file: FileObject
): Promise<void> => {
  let fileRef = file.file;

  Papa.parse(fileRef, {
    complete: async (result: ParseResult<unknown>) => {
      importsUploadStore.update((s) => {
        s.data = result.data.filter((x: any) =>
          Object.values(x).some((value: any) => value.trim() !== "")
        );
        s.columns = result.meta.fields?.map((x) => x.trim());
        s.errors = result.errors;
        s.context = context;
        s.file = file.file;
        s.fileObject = file;
      });
    },
    header: true,
    delimitersToGuess: [",", ";"],
  });
};

const migrationJobService = new MigrationJobService("", axiosInstance);

export const processFileImport = async (
  context: Context,
  file: File,
  importJobTypeId: string,
  columnMappings: FileColumnMapping[]
): Promise<void> => {
  importsBulkUploadStore.update((s) => {
    s.bulkUploadProgressStatus = 0;
    s.showBulkUploadProgress = true;
    s.showCancelledSnackbar = false;
    s.showErrorSnackbar = false;
    s.showSuccessSnackBar = false;
    s.shouldAbortUpload = false;
    s.uploadCompleted = false;
  });

  const progressStatusChunkSize = Math.min(
    (FILE_CHUNK_SIZE * 100) / file.size,
    100
  );

  let importJobId: string = "";
  let rowIndex = 1;

  try {
    let migrationJobResponse = await migrationJobService.createFileJob(
      new CreateFileJobRequestExtended({
        importJobTypeId,
        ...context,
      })
    );

    if (migrationJobResponse.responseCode === ResponseCode.OK) {
      importJobId = migrationJobResponse.id!;
      importsUploadStore.update((s) => {
        s.jobId = importJobId;
      });
    }
  } catch (error) {
    importsBulkUploadStore.update((s) => {
      s.showCancelledSnackbar = true;
    });
    return;
  }

  Papa.parse(file, {
    download: true,
    skipEmptyLines: true,
    fastMode: false,
    quoteChar: '"',
    delimitersToGuess: [",", ";"],
    chunk: async function (chunk, parser) {
      let chunkData = chunk.data;
      if (rowIndex === 1) {
        chunk.data.shift();
      }

      if (importsBulkUploadStore.getRawState().shouldAbortUpload) {
        importsBulkUploadStore.update((s) => {
          s.showCancelledSnackbar = true;
        });
        parser.abort();
        return;
      }

      parser.pause();

      if (chunk.errors.length) {
        importsBulkUploadStore.update((s) => {
          s.shouldAbortUpload = true;
          s.showCancelledSnackbar = true;
          s.cancelledSnackbarMessageId = "bulkUploadFileContentError";
        });
        parser.abort();
        return;
      }

      const data = getNeededData(chunkData, columnMappings);

      const fileDataRequest = new FileDataRequest({
        data,
        columnMappings,
        rowIndex,
      });

      rowIndex += data.length;

      try {
        await migrationJobService.importJobData(importJobId, fileDataRequest);

        importsBulkUploadStore.update((s) => {
          s.bulkUploadProgressStatus =
            s.bulkUploadProgressStatus! + progressStatusChunkSize;
        });
        parser.resume();
      } catch (err) {
        console.error(err);

        importsBulkUploadStore.update((s) => {
          s.shouldAbortUpload = true;
          s.showCancelledSnackbar = true;
          s.cancelledSnackbarMessageId = "bulkUploadNetworkError";
        });
        parser.abort();
        return true;
      }
    },
    chunkSize: FILE_CHUNK_SIZE,
    complete: function () {
      importsBulkUploadStore.update((s) => {
        s.showBulkUploadProgress = false;
      });
      if (!importsBulkUploadStore.getRawState().shouldAbortUpload) {
        importsBulkUploadStore.update((s) => {
          s.showSuccessSnackBar = true;
          s.uploadCompleted = true;
        });
      }
    },
  });
};

const getNeededData = (
  rawData: any[],
  columnMappings: FileColumnMapping[]
): any[][] => {
  const neededData: any[][] = [];

  /*Clear rows which contains only empty values*/
  const sanitized = rawData.filter((x: any) =>
    Object.values(x).some((value: any) => value.trim() !== "")
  );

  sanitized.forEach((element, index) => {
    columnMappings.forEach((mapping) => {
      if (neededData[index] === undefined) neededData[index] = [];

      neededData[index].push(element[mapping.columnIndex!]);
    });
  });

  return neededData;
};

export const closeBulkImportsSuccessSnackbar = () => {
  importsBulkUploadStore.update((s) => {
    s.showSuccessSnackBar = false;
  });
};

export const closeBulkImportsCancelledSnackbar = () => {
  importsBulkUploadStore.update((s) => {
    s.showCancelledSnackbar = false;
  });
};

export const closeBulkImportsErrorSnackbar = () => {
  importsBulkUploadStore.update((s) => {
    s.showErrorSnackbar = false;
  });
};

export const abortBulkImportsUpload = () => {
  importsBulkUploadStore.update((s) => {
    s.shouldAbortUpload = true;
  });
};

export default importsUploadStore;
